<script setup>
import { useStore } from "vuex";
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import { onMounted, ref, computed } from "vue";
import CourseContent from "./components/CourseContent.vue";
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { CoursesClient } from "@/services/CoursesClient";
import { CategoriesClient } from "@/services/CategoriesClient";
import { ResourceClient } from "@/services/ResourceClient";

const router = useRouter();
const route = useRoute();
const courseId = computed(() => route.params.id);

const categoryOptions = ref([]);
const store = useStore();
const coursesClient = new CoursesClient();
const categoriesClient = new CategoriesClient();
const resourceClient = new ResourceClient();
// Form data refs
const courseTitle = ref('');
const courseCategory = ref(''); // Changed from array to single value
const description = ref('');
const currency = ref(0);
const price = ref(0);
const courseImage = ref(null);
const courseStatus = ref(1); // Default to active (1)
const navbarMinimize = () => store.commit("navbarMinimize");
const isUploading = ref(false);
const courseImageId = ref(null); // Store the image ID for deletion
// Handle form submission
const saveCourse = async () => {
  if (!courseTitle.value || !courseCategory.value || price.value === null) {
    Swal.fire({
      icon: 'error',
      title: 'Campos requeridos',
      text: 'Por favor complete todos los campos marcados como obligatorios (*)',
    });
    return;
  }

  Swal.fire({
    title: 'Guardando curso',
    html: 'Por favor espera mientras se guarda tu curso...',
    allowOutsideClick: false,
    didOpen: () => { Swal.showLoading(); }
  });

  try {
    const courseData = {
      titulo: courseTitle.value,
      categoria_id: parseInt(courseCategory.value),
      descripcion: description.value,
      precio: parseFloat(price.value),
      moneda: parseInt(currency.value),
      estado: courseStatus.value // Use the bound value directly
    };

    let successMessage = '';
    
    if (courseId.value) {
      await coursesClient.updateCourse(courseId.value, courseData);
      successMessage = '¡Curso actualizado exitosamente!';
    } else {
      const response = await coursesClient.createCourse(courseData);
      courseId.value = response.id;
      successMessage = '¡Curso creado exitosamente!';
      router.push(`/cursos/editar/${response.id}`);
    }

    Swal.fire({
      icon: 'success',
      title: 'Éxito',
      text: successMessage,
    });

  } catch (error) {
    console.error('Error saving course:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Hubo un problema al guardar el curso. Por favor intenta nuevamente.',
    });
  }
};

// Handle course deletion
const deleteCourse = async () => {
  if (!courseId.value) return;

  const result = await Swal.fire({
    title: '¿Eliminar permanentemente?',
    text: "Todos los datos asociados serán eliminados.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545', // Red
    cancelButtonColor: '#6c757d',  // Gray
    confirmButtonText: 'Eliminar',
    cancelButtonText: 'Cancelar',
    showCancelButton: true,
    buttonsStyling: false,
    customClass: {
      actions: 'grid grid-cols-2 gap-3 w-full',  // Two-column layout
      confirmButton: 'btn btn-danger py-2',       // Danger style
      cancelButton: 'btn btn-outline-secondary py-2' // Outline style
    },
    reverseButtons: true,
    width: '450px' // Wider dialog for better two-column display
  });

  if (result.isConfirmed) {
    try {
      // Show loading state
      Swal.fire({
        title: 'Eliminando curso...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      await coursesClient.deleteCourse(courseId.value)
        .catch(e => {
          if (!e.message.includes('JSON')) throw e;
        });

      // Optional: Redirect after deletion
      router.push('/cursos');

    } catch (error) {
      console.error('Error deleting course:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo eliminar el curso. Por favor intente nuevamente.',
        confirmButtonText: 'Entendido'
      });
    }
  }
};

onMounted(() => {
  navbarMinimize();
  loadCourseData();
  loadCategories(); // Load categories for the select input

});

// Load course data if editing
const loadCourseData = async () => {
  if (courseId.value) {
    try {
      const course = await coursesClient.getCourseById(courseId.value);
      console.log(course);



      // Populate form fields with the loaded data
      courseTitle.value = course.titulo || '';
      description.value = course.descripcion || '';
      currency.value = course.moneda;
      price.value = course.precio || 0;
      courseStatus.value = course.estado;
      courseCategory.value = course.categoria_id ? course.categoria_id.toString() : '';


      if (course.portada?.id) {
        courseImageId.value = course.portada.id;
      }

      // Handle image if available
      if (course.portada?.url) {
        try {
          const imageUrl = await getImageUrl(course.portada.url);
          courseImage.value = imageUrl;
        } catch (error) {
          console.error('Error loading course image:', error);
        }
      }
    } catch (error) {
      console.error('Error loading course:', error);
      Swal.fire('Error', 'Failed to load course data', 'error');
    }
  }
};

const loadCategories = async () => {
  try {
    const categories = await categoriesClient.getAll();

    categoryOptions.value = categories.map(category => ({
      value: category.id.toString(),
      text: category.nombre,
      description: category.descripcion

    }));
  } catch (error) {
    console.error('Error loading categories:', error);
    categoryOptions.value = [];
  }
};

const uploadImage = async (event) => {
  const file = event.target.files[0];
  if (!file) {
    Swal.fire('Error', 'Please select an image file first', 'error');
    return;
  }

  // Check if the file is an image
  if (!file.type.startsWith('image/')) {
    Swal.fire('Error', 'Please select a valid image file (JPEG, PNG, etc.)', 'error');
    return;
  }

  // Show loading alert
  Swal.fire({
    title: 'Subiendo imagen',
    html: 'Por favor espera mientras se sube tu imagen...',
    allowOutsideClick: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });

  try {
    isUploading.value = true;
    const mimeType = file.type;
    const fileName = file.name;

    // Step 1: Get upload URL from backend
    const uploadUrlResponse = await resourceClient.getUploadUrl(mimeType);
    const { url: presignedUrl, key } = uploadUrlResponse;

    // Step 2: Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': mimeType
      }
    });

    if (!uploadResponse.ok) {
      throw new Error('S3 upload failed');
    }

    // Step 3: Create resource record in your database
    await resourceClient.createResource({
      curso_id: courseId.value,
      tipo: mimeType,
      url: key,
      nombre_archivo: fileName,
      es_visualizacion: true
    });

    // Update the displayed image
    courseImage.value = URL.createObjectURL(file);
    loadCourseData(); // Reload course data to ensure the image is updated      

    Swal.fire('Éxito', '¡Imagen subida exitosamente!', 'success');
  } catch (error) {
    console.error('Upload failed:', error);
    Swal.fire('Error', 'Failed to upload image', 'error');
  } finally {
    isUploading.value = false;
    event.target.value = ''; // Reset the file input
  }
};

const getImageUrl = async (s3Key) => {
  const url = `${process.env.VUE_APP_BACK_END_URL}/resource/download-url?key=${s3Key}`;
  const response = await fetch(url, {
    headers: {
      Accept: 'text/plain',
      Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`
    }
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return await response.text(); // This should be the direct image URL
};

async function removeImage() {
  try {

      await resourceClient.deleteResource(courseImageId.value)
    

    courseImage.value = null
    courseImageId.value = null

    const input = document.getElementById('course-image-upload')
    if (input) input.value = ''
  } catch (error) {
    console.error('Error al eliminar la imagen:', error)
  }
}

</script>

<template>
  <!-- Rest of your template remains the same -->
  <div class="p-2 p-lg-3 container-fluid">
    <div class="card">
  <div class="card-body py-3">
    <div class="row">
      <!-- Column 1 (Configuracion del curso) -->
      <div class="col-12 col-md-9 d-flex align-items-center">
        <div>
          <h5 class="mt-0 mb-1"><i class="fas fa-cogs pe-2"></i>Configuración del curso</h5>
          <p v-if="!courseId" class="text-sm mb-0">
            Complete todos la información para crear un nuevo curso
          </p>
          <p v-else class="text-sm mb-0">
            Complete todos la información para editar este curso
          </p>
        </div>
      </div>

      <!-- Column 2 (Buttons) -->
      <div class="col-12 col-md-3 mt-3 mt-md-0">
        <div class="d-flex gap-2 justify-content-end">
          <button type="button" class="btn btn-outline-dark mb-0 w-100" @click="saveCourse">
            <i class="far fa-save pe-2"></i> Guardar
          </button>
          <button type="button" class="btn btn-dark mb-0" @click="deleteCourse" :disabled="!courseId">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

    <div class="mt-2 mt-lg-2 row">
      <div class="col-lg-12">
        <div class="card h-100">
          <div class="card-body">
            <div class="row d-flex justify-content-between">
              <!-- Columna de Información del Producto -->
              <div class="col-12 col-md-6">
                <h5 class="font-weight-bolder"><i class="fas fa-align-left pe-2"></i> Información del Curso</h5>
                <div class="row">
                  <div class="col-12 col-sm-12">
                    <div class="d-flex justify-content-between align-items-center">
                      <label class="mt-3">Título <span class="text-danger">*</span></label>
                      <small class="text-muted ms-4">(máx. 100 caracteres)</small>
                    </div>
                 

                    <input 
                      v-model="courseTitle" 
                      class="form-control text-lg" 
                      type="text" 
                      placeholder="Nombre del Curso"
                      maxlength="80"
                      required 
                    />
                  </div>
                </div>
                <hr class="horizontal dark my-3 my-md-3 mb-md-1" />

                <!-- Categorías (Simple Combobox) -->
                <div class="row">
                  <div class="col-lg-6">
                    <label class="mt-0 mt-md-3">Categoría <span class="text-danger">*</span></label>
                    <select v-model="courseCategory" class="form-control" required>
                      <option value="">Seleccione una categoría</option>
                      <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
                        {{ option.text }} - {{ option.description }}
                      </option>
                    </select>
                  </div>

                  <div class="col-lg-6">
                    <label class="mt-3">Estado</label>
                    <div class="btn-group w-100" role="group" aria-label="Estado del curso">
                      <!-- Hidden State -->
                      <input type="radio" class="btn-check" name="courseStatus" id="oculto" :value="0" v-model="courseStatus">
                      <label class="btn btn-outline-dark btn-sm mb-0 position-relative" for="oculto"
                            :class="{'active': courseStatus === 0}"
                            data-bs-toggle="tooltip" data-bs-placement="top" 
                            title="El curso no será visible para los estudiantes">
                        <i class="far fa-eye-slash me-2"></i>Oculto
                      </label>

                      <!-- Public State -->
                      <input type="radio" class="btn-check" name="courseStatus" id="publico" :value="1" v-model="courseStatus">
                      <label class="btn btn-outline-primary btn-sm mb-0 position-relative" for="publico"
                            :class="{'active': courseStatus === 1}"
                            data-bs-toggle="tooltip" data-bs-placement="top"
                            title="El curso será visible para todos los estudiantes">
                        <i class="far fa-eye me-2"></i>Público
                      </label>
                    </div>
                  </div>

                </div>


                <hr class="horizontal dark my-3 my-md-4" />

                <div class="row mb-5">
                  <label class="mt-1 mt-md-0">Descripción</label>
                  <div id="edit-description-edit">
                    <QuillEditor theme="snow" v-model:content="description" content-type="html" :options="{
                      modules: {
                        toolbar: [
                          ['bold', 'italic', 'underline'],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                        ]
                      }
                    }" />
                  </div>
                </div>
              </div>

              <!-- Columna de Imagen -->
              <div class="col-12 col-md-5 mt-4 mt-md-0">
                <h5 v-if="courseId" class="font-weight-bolder"><i class="fas fa-images pe-2"></i> Portada del Curso</h5>
                <div v-if="courseId"  class="row">
                  <div class="col-12">
                    <img v-if="courseImage" :src="courseImage" class="mt-3 shadow-lg w-100 border-radius-lg"
                      alt="course_image" />

                  </div>

                  <div class="mt-3 col-12">
                    <div class="d-flex">
                      <input type="file" id="course-image-upload" accept="image/*" style="display: none;"
                        @change="uploadImage" />

                      <!-- Mostrar botón "Eliminar Imagen" si hay imagen cargada -->
                      <label v-if="courseImage" @click.prevent="removeImage"
                        class="mb-0 btn btn-sm btn-outline-danger me-2 w-100">
                        <i class="fas fa-trash pe-2"></i> Eliminar Imagen
                      </label>

                      <!-- Mostrar botón "Cargar Imagen" si no hay imagen -->
                      <label v-else for="course-image-upload" class="mb-0 btn btn-sm btn-outline-dark me-2 w-100">
                        <i class="fas fa-upload pe-2"></i> Cargar Imagen
                      </label>
                    </div>
                  </div>
                </div>


                <hr v-if="courseId" class="horizontal dark mt-4" />

                <div class="row mt-4">
                  <h5 class="font-weight-bolder"><i class="fas fa-money-bill pe-2"></i> Vende tu Curso</h5>
                  <div class="col-6 col-lg-4 me-0 me-lg-5">
                    <div>
                      <label>Moneda <span class="text-danger">*</span></label>
                    </div>
                    <div class="btn-group" role="group" aria-label="Moneda">
                      <input type="radio" class="btn-check" name="currency" id="usd" value=1 v-model="currency">
                      <label class="btn btn-sm btn-outline-dark px-3 py-2" for="usd">$ USD</label>

                      <input type="radio" class="btn-check" name="currency" id="col" value=0 v-model="currency">
                      <label class="btn btn-sm btn-outline-dark px-3 py-2" for="col">₡ CRC</label>
                    </div>
                  </div>

                  <div class="col-5 col-lg-6">
                    <label>Precio <span class="text-danger">*</span></label>
                    <input v-model.number="price" class="form-control text-lg" type="number" placeholder="$" required
                      min="0" step="0.01" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="courseId" class="mt-2 mt-lg-2 card2 row">
      <course-content />
    </div>
  </div>
</template>

<style>
.card2 {
  min-height: 800px;
}

.sortable-ghost {
  background: #f8f9fa;
  opacity: 0.7;
}

#edit-description-edit {
  min-height: 150px;
  max-height: 400px;
}
</style>